import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  MapIcon,
  UserGroupIcon,
  CurrencyDollarIcon,
  ClipboardDocumentListIcon,
  ChartBarIcon,
  GlobeAltIcon,
  ArrowDownTrayIcon,
  ShieldCheckIcon,
  WrenchScrewdriverIcon,
  PhotoIcon,
  AdjustmentsHorizontalIcon,
  EnvelopeIcon,
  MapPinIcon,
  TagIcon,
  DevicePhoneMobileIcon,
  BellIcon,
  PaintBrushIcon,
  DocumentArrowUpIcon,
  HeartIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';

const FeaturesPage = () => {
  const [demoEnabled, setDemoEnabled] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loginLogo, setLoginLogo] = useState('/parish-recorder-dark-lg.png');

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const [demoRes, settingsRes] = await Promise.all([
          fetch('/api/demo-mode/status'),
          fetch('/api/site-settings')
        ]);

        if (demoRes.ok) {
          const demoData = await demoRes.json();
          setDemoEnabled(demoData.enabled);
        }

        if (settingsRes.ok) {
          const settingsData = await settingsRes.json();
          setLoginLogo(settingsData.loginLogo || '/parish-recorder-dark-lg.png');
        }
      } catch {
        setDemoEnabled(false);
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
  }, []);

  // Set page title (AppContext is configured to skip this route)
  useEffect(() => {
    document.title = 'Parish Recorder Features - Faithful Stewardship of Cemetery Records';
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-green-600"></div>
      </div>
    );
  }

  if (!demoEnabled) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900">Page Not Available</h1>
          <p className="mt-2 text-gray-600">This page is only available in demo mode.</p>
          <Link to="/login" className="mt-4 inline-block text-brand-green-600 hover:text-brand-green-700 font-medium">
            Go to Login
          </Link>
        </div>
      </div>
    );
  }

  const features = [
    {
      id: 'mapping',
      icon: MapIcon,
      title: 'Interactive Cemetery Mapping',
      subtitle: 'Visualize your entire cemetery with precision GIS mapping',
      description: 'Transform your cemetery records from paper maps and spreadsheets into a dynamic, interactive digital map. Draw, edit, and manage plot boundaries directly on satellite imagery with professional-grade geospatial tools.',
      highlights: [
        'Draw plot polygons directly on the map with point-and-click tools',
        'Color-coded plots showing occupancy status at a glance (vacant, reserved, occupied)',
        'Organize plots by section, alias, and size for easy navigation',
        'Click any plot to instantly view contacts, sales, attachments, and custom data',
        'Search and filter plots with a built-in data table view',
        'Satellite, street, and terrain map styles via Mapbox GL JS',
        'PostGIS-powered spatial queries for precise geographic data',
        'Reserved plot flagging for pre-sold or held plots',
        'Bounding box queries for efficient large-cemetery navigation'
      ]
    },
    {
      id: 'contacts',
      icon: UserGroupIcon,
      title: 'Complete Contact Management',
      subtitle: 'Track every person connected to your cemetery',
      description: 'Maintain comprehensive records for plot owners, family members, and those laid to rest. Link contacts to plots with specific relationship types, track veteran status, and keep all correspondence details organized.',
      highlights: [
        'Full contact records with name, address, email, phone, and photo',
        'Relationship types: Owner, Co-Owner, Family Member, Interred, Beneficiary',
        'Veteran status tracking with map indicators for veteran grave sites',
        'Paginated, searchable, and sortable contact directory',
        'Filter contacts by plot associations (with or without plots)',
        'View all plots and sales associated with each contact',
        'Custom contact statuses for workflow management',
        'Related contacts discovery through shared plot associations',
        'Custom fields for organization-specific data (membership IDs, affiliations, etc.)'
      ]
    },
    {
      id: 'sales',
      icon: CurrencyDollarIcon,
      title: 'Sales & Payment Tracking',
      subtitle: 'Complete financial management for cemetery services',
      description: 'Track every transaction from initial quote through final payment. Automatic status calculations, payment recording, due date monitoring, and invoice generation keep your finances organized and transparent.',
      highlights: [
        'Create sales with multiple line items from your service catalog',
        'Automatic status tracking: Draft, Quoted, Partially Paid, Fully Paid, Cancelled, Refunded',
        'Record payments by check, card, or cash with instant balance updates',
        'Stripe integration available for online payment processing',
        'Due date monitoring with overdue sale alerts',
        'Link sales to one or more plots for complete transaction history',
        'Status history tracking shows every status change with timestamps',
        'Email invoice generation and sending to contacts',
        'Advanced filtering by status, date range, product, and unpaid balance',
        'Custom fields for reference numbers, approval tracking, and more'
      ]
    },
    {
      id: 'products',
      icon: TagIcon,
      title: 'Product & Service Catalog',
      subtitle: 'Organize and price your cemetery services',
      description: 'Define all the products and services your cemetery offers, from plot sales and interment services to maintenance packages and memorial items. Categorize with tags for easy organization and filtering.',
      highlights: [
        'Create services with name, price, and detailed descriptions',
        'Tag-based categorization for organizing large service catalogs',
        'Filter services by tag for quick access to related offerings',
        'Automatic price population when adding items to sales',
        'Adjustable per-sale pricing for discounts and special rates',
        'Multiple tags per service for cross-category organization',
        'Public pricing display option for the public portal'
      ]
    },
    {
      id: 'work-orders',
      icon: ClipboardDocumentListIcon,
      title: 'Work Order Management',
      subtitle: 'Coordinate maintenance, preparation, and grounds work',
      description: 'Create, assign, and track work orders for all cemetery operations. From funeral preparation to landscaping, keep your team organized with priorities, assignments, status tracking, and shareable public links for external vendors.',
      highlights: [
        'Six work order types: Funeral Preparation, Headstone Installation, Repair, Maintenance, Landscaping, Other',
        'Priority levels: Low, Medium, High, Urgent',
        'Assign to specific team members with notification alerts',
        'Customizable status workflow (New, In Progress, On Hold, Completed, Cancelled, or your own)',
        'Public status update links for external vendors and contractors (no login required)',
        'Photo and document attachments for before/after documentation',
        'Notes system for progress tracking and communication',
        'Link work orders to specific plots and contacts',
        'Due date tracking for deadline management',
        'Grounds Crew role for field staff with limited access (view/update assigned work only)',
        'Status history with full audit trail of changes',
        'In-app and email notifications on status changes'
      ]
    },
    {
      id: 'custom-fields',
      icon: AdjustmentsHorizontalIcon,
      title: 'Custom Fields',
      subtitle: 'Extend your data model without code changes',
      description: 'Define additional fields for plots, contacts, and sales to capture any information unique to your organization. Choose from multiple field types and control visibility on the public portal.',
      highlights: [
        'Six field types: Text, Number, Date, Dropdown Select, Textarea, Checkbox',
        'Apply to Plots, Contacts, or Sales independently',
        'Define dropdown options for consistent data entry',
        'Mark fields as required for data completeness',
        'Public/private toggle controls visibility on the public portal',
        'Custom sort ordering for logical field arrangement',
        'Automatic mapping during geospatial data import',
        'Full export/import support for backup and migration'
      ]
    },
    {
      id: 'attachments',
      icon: PhotoIcon,
      title: 'Plot Attachments',
      subtitle: 'Visual documentation for every plot',
      description: 'Attach photos, documents, and links directly to plots. Document headstones, deeds, agreements, and obituaries with full control over public visibility.',
      highlights: [
        'Three attachment types: Images, Documents, and External Links',
        'Drag-and-drop file upload with preview thumbnails',
        'Public/private visibility toggle per attachment',
        'Image gallery with fullscreen viewing on the public portal',
        'Sortable attachments for custom ordering',
        'Support for headstone photos, burial deeds, memorial documents',
        'External links to obituaries, family sites, or other resources',
        'Full export/import with base64-encoded file preservation'
      ]
    },
    {
      id: 'analytics',
      icon: ChartBarIcon,
      title: 'Analytics & Reporting',
      subtitle: 'Data-driven insights for cemetery management',
      description: 'Comprehensive analytics dashboard with sales trends, payment tracking, plot statistics, and contact metrics. Export reports as PDF for board meetings and financial reviews.',
      highlights: [
        'Sales revenue charts with daily, weekly, and monthly grouping',
        'Total revenue, paid, and unpaid balance at a glance',
        'Status breakdown showing sale distribution across statuses',
        'Overdue payment identification and tracking',
        'Plot statistics: total, occupied, reserved, vacant',
        'Contact metrics and growth tracking',
        'Product popularity analysis',
        'Configurable date range with preset filters',
        'PDF export for offline reports and presentations'
      ]
    },
    {
      id: 'public-portal',
      icon: GlobeAltIcon,
      title: 'Public Portal',
      subtitle: 'Share your cemetery map with visitors and families',
      description: 'Offer a public-facing interactive map where visitors can search for grave sites, view plot details, browse documents, and find pricing information - all without needing an account.',
      highlights: [
        'Interactive searchable map with plot popups',
        'Search by name, plot alias, or section',
        'Configurable visibility: control exactly what the public sees',
        'Contact names, dates, veteran status visibility toggles',
        'Plot availability statistics by section',
        'Public document library for rules, regulations, and forms',
        'Optional pricing/services display with tag filtering',
        'Custom welcome message and contact information',
        'Plot image galleries visible on public detail pages',
        'Custom field visibility on public-facing plot details',
        'Privacy-first: personal addresses and phone numbers never shown publicly'
      ]
    },
    {
      id: 'obituaries',
      icon: HeartIcon,
      title: 'Obituary Management',
      subtitle: 'Honor and memorialize those laid to rest',
      description: 'Create beautiful obituary pages to commemorate and share memories of those interred in your cemetery. Link obituaries to contacts, attach photos, and share publicly with customizable privacy controls.',
      highlights: [
        'Create detailed obituaries with biographical information',
        'Upload multiple life photos and documents',
        'Service information: date, location, and notes',
        'Memorial donation and condolence address details',
        'Draft and publish workflow - save progress before publishing',
        'Private/public visibility toggle - keep sensitive obituaries private',
        'Unique shareable deeplinks for each obituary',
        'Optionally link obituaries to existing contacts',
        'Show obituary links on public plot details for interred individuals',
        'Configurable visibility settings for all obituary sections',
        'Search and filter obituary listings',
        'Full export/import support for backup and migration'
      ]
    },
    {
      id: 'documents',
      icon: DocumentTextIcon,
      title: 'Document Generation',
      subtitle: 'Create professional contracts, invoices, and letters',
      description: 'Design custom document templates with dynamic data fields that automatically populate from your contacts, sales, plots, and work orders. Generate professional PDFs for contracts, invoices, receipts, and correspondence with one click.',
      highlights: [
        'Create reusable document templates with HTML and CSS styling',
        'Dynamic variables: insert contact names, addresses, sale totals, plot details',
        'Template categories: contracts, invoices, receipts, letters, reports, work orders',
        'Live preview with sample data before generating',
        'Generate PDFs from any entity: contacts, sales, plots, work orders',
        'Email documents directly to contacts from the application',
        'Version history with rollback to previous template versions',
        'Page settings: orientation, size, margins, headers, and footers',
        'Handlebars templating with formatCurrency, formatDate, and other helpers',
        'Store generated documents linked to contacts for audit trail',
        'Download or print generated documents instantly',
        'Full export/import support for templates and generated documents'
      ]
    },
    {
      id: 'data-management',
      icon: ArrowDownTrayIcon,
      title: 'Data Export & Import',
      subtitle: 'Full backup and migration capabilities',
      description: 'Export your entire cemetery database as a ZIP archive of CSV files. Import previously exported data for migration, backup restoration, or cross-system transfers. Supports 26+ data types with full relationship preservation.',
      highlights: [
        'One-click full database export as ZIP containing CSV files',
        '26+ data types exported: plots, contacts, sales, work orders, and more',
        'Complete relationship preservation across all linked records',
        'PostGIS geometry data preserved in GeoJSON format',
        'JSONB fields (status history, metadata) round-trip correctly',
        'Base64-encoded file attachments included in exports',
        'Upsert logic on import: updates existing records, creates new ones',
        'Foreign key constraint-respecting import order',
        'Open CSV format compatible with Excel, Google Sheets, and databases',
        'Email templates, custom fields, and work orders all included'
      ]
    },
    {
      id: 'geospatial-import',
      icon: MapPinIcon,
      title: 'Geospatial Import',
      subtitle: 'Bring in existing GIS data from any source',
      description: 'Import plot boundaries from industry-standard geospatial formats. Whether you have shapefiles from a surveyor, KML from Google Earth, or GeoJSON from another system, bring your existing plot data in with full attribute mapping.',
      highlights: [
        'Shapefile (.shp/.zip) import with attribute mapping',
        'KML/KMZ support for Google Earth data',
        'GeoJSON direct import for web-standard data',
        'CSV import with WKT or GeoJSON geometry columns',
        'Map source attributes to plot fields and custom fields',
        'Geometry validation and automatic repair',
        'Coordinate projection handling for different spatial references',
        'Bulk plot creation with automatic ID assignment',
        '50MB file size support for large datasets'
      ]
    },
    {
      id: 'email',
      icon: EnvelopeIcon,
      title: 'Email System',
      subtitle: 'Automated communications with template management',
      description: 'Send invoices, payment confirmations, and notifications with a fully integrated email system. Manage templates, track delivery, and monitor usage with built-in logging and statistics.',
      highlights: [
        'Resend API integration for reliable email delivery',
        'Template-based emails with variable substitution',
        'Invoice email generation from sales with line items and totals',
        'Payment confirmation emails on receipt',
        'Password reset and account notification emails',
        'Email send logs with delivery status tracking',
        'Custom template creation for organization-specific communications',
        'Email quota monitoring and usage statistics',
        'System templates for core functionality, custom templates for your needs'
      ]
    },
    {
      id: 'security',
      icon: ShieldCheckIcon,
      title: 'Security & Access Control',
      subtitle: 'Role-based permissions at every level',
      description: 'Multi-layered security with global roles and secure authentication. Control who can view, edit, or administer each cemetery in your system.',
      highlights: [
        'JWT-based authentication with HTTP-only cookies',
        'bcrypt password hashing for secure credential storage',
        'Global roles: Admin for full access, User for standard access, Grounds Crew for field staff',
        'Users see only cemetery data they have access to',
        'Password reset with secure email tokens',
        'Admin user management with role assignment',
        'Account deactivation without data loss',
        'Self-protection: admins cannot remove their own access'
      ]
    },
    {
      id: 'customization',
      icon: PaintBrushIcon,
      title: 'Site Customization',
      subtitle: 'Brand the application for your organization',
      description: 'Customize the application with your organization\'s name, logos, and color scheme. Make the software feel like it was built specifically for your cemetery.',
      highlights: [
        'Custom site name displayed throughout the application',
        'Upload custom header logo for the navigation bar',
        'Upload custom login page logo for branded sign-in',
        'Customizable plot colors: vacant, reserved, occupied, highlight',
        'Toggle plot size display on the map',
        'SVG, PNG, JPG, GIF, and WebP logo format support',
        'Reset to default branding at any time',
        'Dynamic PWA manifest with custom name and icons'
      ]
    },
    {
      id: 'pwa',
      icon: DevicePhoneMobileIcon,
      title: 'Progressive Web App',
      subtitle: 'Works on any device, installs like a native app',
      description: 'Access your cemetery management system from any device with a modern web browser. Install it on your phone or tablet for quick access in the field, with automatic update notifications when new versions are available.',
      highlights: [
        'Install on iOS, Android, Windows, or macOS as a standalone app',
        'Responsive design optimized for desktop, tablet, and mobile',
        'Touch-optimized map controls for field use on phones and tablets',
        'Automatic update notifications when new versions are deployed',
        'Service worker for improved performance and caching',
        'Works on any device with a modern web browser',
        'No app store required - install directly from the browser'
      ]
    },
    {
      id: 'notifications',
      icon: BellIcon,
      title: 'In-App Notifications',
      subtitle: 'Stay informed about work order updates and assignments',
      description: 'Receive real-time notifications when work orders are assigned to you, when statuses change, or when notes are added. Never miss an important update.',
      highlights: [
        'Work order assignment notifications',
        'Status change alerts for orders you created or are assigned to',
        'Unread count badge in the navigation bar',
        'Mark individual or all notifications as read',
        'Notification history with pagination',
        'Filter to show only unread notifications',
        'Automatic notifications for public status updates from vendors'
      ]
    },
    {
      id: 'geospatial-export',
      icon: DocumentArrowUpIcon,
      title: 'Geospatial Data Tools',
      subtitle: 'Professional GIS capabilities built in',
      description: 'Powered by PostGIS, the industry-standard spatial database, your cemetery data is stored with full geographic precision. Perform spatial queries, calculate areas, and maintain coordinate-accurate plot boundaries.',
      highlights: [
        'PostGIS GEOMETRY(MultiPolygon) storage for precise boundaries',
        'WGS84 (EPSG:4326) coordinate reference system',
        'Spatial queries with bounding box filtering',
        'Centroid calculations for plot label placement',
        'Area calculation in square feet for plot sizing',
        'GeoJSON export for use in other GIS software',
        'Spatial indexing for fast queries on large cemeteries',
        'Multi-polygon support for complex plot shapes'
      ]
    },
    {
      id: 'google-analytics',
      icon: ChartBarIcon,
      title: 'Google Analytics Integration',
      subtitle: 'Track visitor activity with your own Google tag',
      description: 'Connect your Google Analytics account to track how visitors interact with your site, including the public portal. Simply enter your Google Tag ID in Site Settings and analytics tracking is automatically enabled across all pages.',
      highlights: [
        'Easy setup — just paste your Google Tag ID',
        'Track public portal visitor activity and search patterns',
        'Monitor page views, session duration, and user engagement',
        'Understand how families find and use your cemetery information',
        'Works with Google Analytics 4 (GA4) measurement IDs',
        'No code changes required — configure entirely from Site Settings',
        'Supports Google Tag (GT-) and Google Ads (AW-) tags as well'
      ]
    }
  ];

  const quickFeatures = [
    { label: 'Interactive GIS Map', icon: MapIcon },
    { label: 'Contact Management', icon: UserGroupIcon },
    { label: 'Sales & Payments', icon: CurrencyDollarIcon },
    { label: 'Obituaries', icon: HeartIcon },
    { label: 'Document Generation', icon: DocumentTextIcon },
    { label: 'Work Orders', icon: ClipboardDocumentListIcon },
    { label: 'Custom Fields', icon: AdjustmentsHorizontalIcon },
    { label: 'Plot Attachments', icon: PhotoIcon },
    { label: 'Analytics & PDF Reports', icon: ChartBarIcon },
    { label: 'Public Portal', icon: GlobeAltIcon },
    { label: 'Data Export/Import', icon: ArrowDownTrayIcon },
    { label: 'Geospatial Import', icon: MapPinIcon },
    { label: 'Email Templates', icon: EnvelopeIcon },
    { label: 'Role-Based Security', icon: ShieldCheckIcon },
    { label: 'Custom Branding', icon: PaintBrushIcon },
    { label: 'Mobile Responsive', icon: DevicePhoneMobileIcon },
    { label: 'In-App Notifications', icon: BellIcon },
    { label: 'Service Catalog & Tags', icon: TagIcon },
    { label: 'Google Analytics', icon: ChartBarIcon }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={loginLogo} alt="Parish Recorder" className="h-10 w-auto" />
            <span className="text-xl font-semibold text-gray-900">Parish Recorder</span>
          </div>
          <div className="flex items-center gap-4">
            <a
              href="https://parishrecorder.com/shop/"
              className="text-sm font-medium text-gray-600 hover:text-gray-900"
            >
              Pricing
            </a>
            <a
              href="https://demo.parishrecorder.com/"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-brand-green-600 hover:bg-brand-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-green-500"
            >
              Try the Demo
            </a>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-b from-brand-green-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-gray-900">
              Modern Cemetery Management,{' '}
              <span className="text-brand-green-600">Simplified</span>
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto">
              Replace paper records, spreadsheets, and outdated software with an all-in-one platform
              built specifically for cemetery administrators. Interactive mapping, complete record
              management, financial tracking, and a public portal - all in one place.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/login"
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-brand-green-600 hover:bg-brand-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-green-500"
              >
                Try the Demo
              </Link>
              <a
                href="#features"
                className="inline-flex items-center px-6 py-3 border border-gray-300 text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-green-500"
              >
                Explore Features
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Feature Grid */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl font-bold text-gray-900">Everything You Need in One Platform</h2>
            <p className="mt-2 text-gray-600">Purpose-built tools for modern cemetery management</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {quickFeatures.map((feature, idx) => (
              <div key={idx} className="flex flex-col items-center gap-2 p-4 bg-white rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                <feature.icon className="w-6 h-6 text-brand-green-600" />
                <span className="text-xs sm:text-sm text-center font-medium text-gray-700">{feature.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Features */}
      <section id="features" className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900">Powerful Features in Detail</h2>
            <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
              Every tool you need to manage a modern cemetery, from interactive mapping to
              financial tracking and public access.
            </p>
          </div>

          <div className="space-y-20">
            {features.map((feature, idx) => (
              <div
                key={feature.id}
                className={`flex flex-col ${idx % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-8 lg:gap-16 items-start`}
              >
                {/* Feature Info */}
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-brand-green-100 rounded-xl flex items-center justify-center">
                      <feature.icon className="w-6 h-6 text-brand-green-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">{feature.title}</h3>
                      <p className="text-sm text-brand-green-600 font-medium">{feature.subtitle}</p>
                    </div>
                  </div>
                  <p className="text-gray-600 mb-6">{feature.description}</p>
                  <ul className="space-y-2">
                    {feature.highlights.map((highlight, hIdx) => (
                      <li key={hIdx} className="flex items-start gap-2">
                        <svg className="w-5 h-5 text-brand-green-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        <span className="text-sm text-gray-700">{highlight}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Feature Visual */}
                <div className="flex-1 w-full">
                  <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-8 border border-gray-200 min-h-[240px] flex items-center justify-center">
                    <div className="text-center">
                      <feature.icon className="w-16 h-16 text-brand-green-300 mx-auto mb-3" />
                      <p className="text-sm text-gray-400 font-medium">{feature.title}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-green-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white">Ready to Modernize Your Cemetery Records?</h2>
          <p className="mt-4 text-lg text-brand-green-100">
            Join cemeteries that have moved beyond paper records and spreadsheets.
            Start managing your grounds with professional-grade tools built for your needs.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/login"
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-brand-green-600 bg-white hover:bg-brand-green-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-brand-green-600 focus:ring-white"
            >
              Try the Demo Now
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <img src={loginLogo} alt="Parish Recorder" className="h-8 w-auto brightness-200" />
              <span className="text-white font-medium">Parish Recorder</span>
            </div>
            <div className="flex items-center gap-6">
              <a href="https://parishrecorder.com/shop/" className="text-sm text-gray-400 hover:text-white">Pricing</a>
              <Link to="/public" className="text-sm text-gray-400 hover:text-white">Public Portal</Link>
            </div>
          </div>
          <div className="mt-8 border-t border-gray-800 pt-8">
            <p className="text-center text-sm text-gray-500">
              Comprehensive cemetery management software with interactive mapping, contact management, sales tracking, and more.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default FeaturesPage;
